<template>
  <div class="mt-4 flex w-full flex-row items-end">
    <div
      v-for="item in items"
      :key="item.value"
      class="flex h-28 min-w-0 grow basis-0 flex-col items-center"
    >
      <div class="flex min-h-0 w-full flex-1 items-end gap-0.5 px-3">
        <div class="flex h-full min-w-0 flex-1 flex-col justify-end">
          <span class="text-label-medium text-center" :class="countClass(item.count)">{{
            item.count
          }}</span>
          <div
            class="ease-fast-spatial rounded-b-extra-small min-h-1 w-full shrink-0 transition-all duration-200"
            :class="[
              item.count > 0 &&
                colorClassesForPerspective(SELF_PERSPECTIVE, [
                  SELF_PERSPECTIVE,
                  TEACHER_PERSPECTIVE,
                ]),
              item.count === 0 && 'bg-surface-container-highest',
              item.teacherCount != null && 'rounded-t-small',
              item.teacherCount == null && 'rounded-t-large',
            ]"
            :style="{ height: `${barHeight(item.count)}%` }"
          />
        </div>
        <div
          v-if="item.teacherCount != null"
          class="flex h-full min-w-0 flex-1 flex-col justify-end"
        >
          <span class="text-label-medium text-center" :class="countClass(item.teacherCount)">{{
            item.teacherCount
          }}</span>
          <div
            class="ease-fast-spatial rounded-b-extra-small min-h-1 w-full shrink-0 transition-all duration-200"
            :class="[
              item.teacherCount > 0 &&
                colorClassesForPerspective(TEACHER_PERSPECTIVE, [
                  SELF_PERSPECTIVE,
                  TEACHER_PERSPECTIVE,
                ]),
              item.teacherCount === 0 && 'bg-surface-container-highest',
              item.teacherCount != null && 'rounded-t-small',
              item.teacherCount == null && 'rounded-t-large',
            ]"
            :style="{
              height: `${teacherBarHeight(item.teacherCount)}%`,
            }"
          />
        </div>
      </div>
      <div class="">
        <ScaleValueDotsLegend :items="items" :value="item.value" class="bg-transparent!" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed, onMounted, ref, watch } from 'vue'
  import ScaleValueDotsLegend from './ScaleValueDotsLegend.vue'
  import { colorClassesForPerspective } from '@/core/models/feedback-perspective.ts'
  import { TEACHER_PERSPECTIVE } from '../models/kompetenzprofil.ts'
  import { SELF_PERSPECTIVE } from '@/core/models/perspective.ts'

  export interface IndikatorHistogramItem {
    readonly value: number
    readonly count: number
    readonly teacherCount?: number
  }

  const props = defineProps<{
    items: ReadonlyArray<IndikatorHistogramItem>
    /** Highest answer count of any single scale item. That count is full height. */
    maxAnswers: number
  }>()

  const countClass = (count: number) =>
    count > 0 ? 'text-on-surface-variant' : 'text-outline-variant'

  const barHeight = (count: number) => {
    if (!animate.value) return 0
    if (count <= 0 || props.maxAnswers <= 0) return 0
    return Math.min(100, (count / props.maxAnswers) * 100)
  }

  const teacherBarHeight = (count: number) => {
    if (!animate.value || !teacherShown.value) return 0
    if (count <= 0 || props.maxAnswers <= 0) return 0
    return Math.min(100, (count / props.maxAnswers) * 100)
  }

  const animate = ref(false)
  const teacherShown = ref(false)

  const hasTeacherAssessment = computed(() => {
    return props.items.some((item) => item.teacherCount != null)
  })

  watch(hasTeacherAssessment, (value) => {
    if (value) {
      setTimeout(() => {
        teacherShown.value = true
      }, 10)
    } else {
      teacherShown.value = false
    }
  })

  onMounted(() => {
    setTimeout(() => {
      animate.value = true
    }, 10)
  })
</script>
